import React, { useState, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PaletteIcon from "@mui/icons-material/Palette";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const FOLHA_W = 320, FOLHA_H = 450;
const PALETA = ["#9e9e9e", "#e74c3c", "#3498db", "#2ecc71", "#f39c12", "#9b59b6", "#1abc9c", "#e67e22", "#34495e", "#c0392b"];

const itemVazio = () => ({
  arteFileName: null, arteUrl: null,
  tipo: "redondo", larguraMm: 50, alturaMm: 50, raioCantoMm: 3, quantidade: 10,
  ajuste: "preencher", escalaPct: 100, offsetXMm: 0, offsetYMm: 0,
});

const AJUSTES = [
  { value: "preencher", label: "Preencher (corta sobras)" },
  { value: "proporcional", label: "Proporcional (cabe inteira)" },
  { value: "esticar", label: "Esticar (distorce)" },
  { value: "centro", label: "Centro (tamanho real)" },
];

// Mini-formulário: dado uma arte já enviada, o usuário monta N linhas de
// {cor, quantidade}. Ao confirmar, chama /adesivos/variantes-cor e devolve
// as variantes geradas (cada uma vira 1 item novo da cartela lá fora).
const varianteLinhaVazia = () => ({ corHex: "#000000", quantidade: 10 });

const VariantesCorDialog = ({ open, onClose, arteFileName, onGeradas }) => {
  const [linhas, setLinhas] = useState([varianteLinhaVazia()]);
  const [gerando, setGerando] = useState(false);

  useEffect(() => {
    if (open) setLinhas([varianteLinhaVazia()]);
  }, [open]);

  const setLinha = (i, campo, val) =>
    setLinhas((ls) => ls.map((l, j) => (j === i ? { ...l, [campo]: val } : l)));
  const addLinha = () => setLinhas((ls) => [...ls, varianteLinhaVazia()]);
  const rmLinha = (i) => setLinhas((ls) => (ls.length > 1 ? ls.filter((_, j) => j !== i) : ls));

  const handleGerar = async () => {
    setGerando(true);
    try {
      const variantes = linhas.map((l) => ({ corHex: l.corHex, quantidade: Math.max(1, Number(l.quantidade) || 1) }));
      const { data } = await api.post("/adesivos/variantes-cor", { arteFileName, variantes });
      onGeradas(data);
      toast.success(`${data.length} variante(s) de cor adicionada(s) à cartela.`);
      onClose();
    } catch (err) { toastError(err); }
    finally { setGerando(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Gerar variantes de cor</DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
          Mesma arte, recolorida automaticamente para cada cor abaixo — cada linha vira 1 item
          da cartela, com sua própria quantidade.
        </Typography>
        {linhas.map((l, i) => (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
            <input
              type="color"
              value={l.corHex}
              onChange={(e) => setLinha(i, "corHex", e.target.value)}
              style={{ width: 40, height: 40, border: "1px solid #ccc", borderRadius: 4, padding: 0, cursor: "pointer" }}
            />
            <TextField label="Cor (hex)" size="small" value={l.corHex}
              onChange={(e) => setLinha(i, "corHex", e.target.value)} sx={{ width: 110 }} />
            <TextField label="Qtd" type="number" size="small" value={l.quantidade}
              onChange={(e) => setLinha(i, "quantidade", e.target.value)} sx={{ width: 90 }} />
            <IconButton size="small" onClick={() => rmLinha(i)}><DeleteOutlineIcon fontSize="small" /></IconButton>
          </div>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={addLinha}>Adicionar cor</Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleGerar} disabled={gerando || linhas.length === 0}
          startIcon={gerando ? <CircularProgress size={16} /> : <PaletteIcon />}>
          Gerar {linhas.length} variante(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CartelaDialog = ({ open, onClose, adesivoId, onSaved, initialTicketId, initialContact }) => {
  const contatoVinculado = initialContact || null;

  const [savedId, setSavedId] = useState(adesivoId || null);
  const [savedData, setSavedData] = useState(null);
  // Ticket vinculado: da gaveta OU do próprio pedido salvo (editando pela lista).
  const ticketId = initialTicketId || savedData?.ticketId || null;
  const [loading, setLoading] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [enviandoProva, setEnviandoProva] = useState(false);
  const [aprovando, setAprovando] = useState(false);

  const [descricao, setDescricao] = useState("");
  const [mesmaFolha, setMesmaFolha] = useState(true);
  const [material, setMaterial] = useState("papel");
  const [acabamento, setAcabamento] = useState("nenhum");
  const [opcoes, setOpcoes] = useState({ materiais: [], acabamentos: [] });
  const [espacoMm, setEspacoMm] = useState(3);
  const [sangriaMm, setSangriaMm] = useState(2);
  const [margemMarcasMm, setMargemMarcasMm] = useState(12);
  const [precoFolha, setPrecoFolha] = useState("");
  const [corteOffsetYMm, setCorteOffsetYMm] = useState(-1);
  const [itens, setItens] = useState([itemVazio()]);
  const [enviandoArte, setEnviandoArte] = useState(null); // idx sendo enviado
  const [variantesOrigemIdx, setVariantesOrigemIdx] = useState(null); // idx do item base p/ gerar variantes de cor

  const [preview, setPreview] = useState({ folhas: [], numFolhas: 0, totalColocado: 0, faltando: 0, valorTotal: 0 });
  const [folhaSel, setFolhaSel] = useState(0);
  const [pltInfo, setPltInfo] = useState(null); // { numFolhas, cortesIguais }

  useEffect(() => {
    api.get("/adesivos/opcoes").then(({ data }) => setOpcoes(data)).catch(() => {});
  }, []);

  // Info do corte (igual/diferente por folha) sempre que houver arquivos gerados.
  useEffect(() => {
    if (savedId && savedData?.arquivoPltFileName) {
      api.get(`/adesivos/${savedId}/plt-info`).then(({ data }) => setPltInfo(data)).catch(() => setPltInfo(null));
    } else {
      setPltInfo(null);
    }
  }, [savedId, savedData?.arquivoPltFileName]);

  const corpoBody = () => ({
    modo: "cartela",
    descricao, mesmaFolha, material, acabamento,
    espacoMm, sangriaMm, margemMarcasMm, precoFolha: Number(precoFolha) || 0,
    bladeMm: 0.35, overcutMm: 1, corteOffsetXMm: 0, corteOffsetYMm,
    itens: itens.filter((it) => it.arteFileName).map((it) => ({
      arteFileName: it.arteFileName, tipo: it.tipo,
      larguraMm: it.larguraMm, alturaMm: it.alturaMm, raioCantoMm: it.raioCantoMm,
      quantidade: it.quantidade,
      ajuste: it.ajuste || "preencher", escalaPct: Number(it.escalaPct) || 100,
      offsetXMm: Number(it.offsetXMm) || 0, offsetYMm: Number(it.offsetYMm) || 0,
    })),
    ...(ticketId ? { ticketId } : {}),
    ...(contatoVinculado?.id ? { contactId: contatoVinculado.id } : {}),
  });

  // Preview ao vivo (debounce) sempre que itens/params mudam.
  const recalcular = useCallback(async () => {
    const body = corpoBody();
    if (body.itens.length === 0) { setPreview({ folhas: [], numFolhas: 0, totalColocado: 0, faltando: 0, valorTotal: 0 }); return; }
    try {
      const { data } = await api.post("/adesivos/cartela-preview", body);
      setPreview(data);
      setFolhaSel((s) => Math.min(s, Math.max(0, data.numFolhas - 1)));
    } catch { /* silencioso */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itens, mesmaFolha, espacoMm, sangriaMm, margemMarcasMm, precoFolha]);

  useEffect(() => {
    const t = setTimeout(recalcular, 350);
    return () => clearTimeout(t);
  }, [recalcular]);

  const loadAdesivo = useCallback(async (id) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/adesivos/${id}`);
      setSavedData(data);
      setDescricao(data.descricao || "");
      const c = data.config || {};
      setMesmaFolha(c.mesmaFolha !== false);
      setMaterial(c.material || "papel");
      setAcabamento(c.acabamento || "nenhum");
      setEspacoMm(c.espacoMm ?? 3);
      setSangriaMm(c.sangriaMm ?? 2);
      setMargemMarcasMm(c.margemMarcasMm ?? 12);
      setPrecoFolha(c.precoFolha ? String(c.precoFolha) : "");
      setCorteOffsetYMm(c.corteOffsetYMm ?? -1);
      const its = Array.isArray(c.itens) ? c.itens : [];
      const carregados = await Promise.all(its.map(async (it) => {
        let arteUrl = null;
        try {
          const resp = await api.get(`/adesivos/arte/${it.arteFileName}`, { responseType: "blob" });
          arteUrl = URL.createObjectURL(resp.data);
        } catch { /* sem preview */ }
        return { ...itemVazio(), ...it, arteUrl };
      }));
      setItens(carregados.length ? carregados : [itemVazio()]);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!open) return;
    setSavedId(adesivoId || null);
    setSavedData(null);
    if (adesivoId) loadAdesivo(adesivoId);
    else { setDescricao(""); setItens([itemVazio()]); setMesmaFolha(true); setPrecoFolha(""); setCorteOffsetYMm(-1); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, adesivoId, loadAdesivo]);

  const setItem = (i, campo, val) => setItens((its) => its.map((t, j) => (j === i ? { ...t, [campo]: val } : t)));
  const setItemNum = (i, campo) => (e) => setItem(i, campo, e.target.value === "" ? "" : Number(e.target.value));
  const addItem = () => setItens((its) => [...its, itemVazio()]);
  const rmItem = (i) => setItens((its) => its.length > 1 ? its.filter((_, j) => j !== i) : its);

  const uploadArte = (i) => async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setEnviandoArte(i);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/adesivos/arte", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const resp = await api.get(`/adesivos/arte/${data.fileName}`, { responseType: "blob" });
      setItens((its) => its.map((t, j) => j === i ? { ...t, arteFileName: data.fileName, arteUrl: URL.createObjectURL(resp.data) } : t));
    } catch (err) { toastError(err); }
    finally { setEnviandoArte(null); }
  };

  // Recebe as variantes geradas pelo backend (POST /adesivos/variantes-cor) e
  // adiciona 1 item novo à cartela por variante, herdando forma/tamanho/ajuste
  // do item de origem (só troca arteFileName, quantidade e a miniatura).
  const handleVariantesGeradas = useCallback(async (variantes) => {
    if (variantesOrigemIdx === null) return;
    const base = itens[variantesOrigemIdx];
    if (!base) return;
    const novos = await Promise.all(variantes.map(async (v) => {
      let arteUrl = null;
      try {
        const resp = await api.get(`/adesivos/arte/${v.arteFileName}`, { responseType: "blob" });
        arteUrl = URL.createObjectURL(resp.data);
      } catch { /* sem preview */ }
      return {
        ...base,
        arteFileName: v.arteFileName,
        arteUrl,
        quantidade: v.quantidade,
      };
    }));
    setItens((its) => [...its, ...novos]);
  }, [variantesOrigemIdx, itens]);

  const ensureSaved = async () => {
    if (savedId) { await api.put(`/adesivos/${savedId}`, corpoBody()); return savedId; }
    const { data } = await api.post("/adesivos", corpoBody());
    setSavedId(data.id);
    return data.id;
  };

  const handleSalvar = async () => {
    setLoading(true);
    try { await ensureSaved(); toast.success("Rascunho salvo."); onSaved && onSaved(); }
    catch (err) { toastError(err); } finally { setLoading(false); }
  };

  const handleGerar = async () => {
    if (corpoBody().itens.length === 0) { toast.warn("Adicione ao menos uma arte."); return; }
    setGerando(true);
    try {
      const id = await ensureSaved();
      const { data } = await api.post(`/adesivos/${id}/generate`, corpoBody());
      setSavedData(data);
      toast.success(`Gerado: ${data.folhas} folha(s).`);
      onSaved && onSaved();
    } catch (err) { toastError(err); } finally { setGerando(false); }
  };

  const baixar = async (tipo) => {
    if (!savedId) return;
    try {
      const { data } = await api.get(`/adesivos/${savedId}/${tipo}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (tipo === "conferencia") { window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 30000); return; }
      const a = document.createElement("a");
      a.href = url; a.download = `${savedData?.numero || "cartela"}.${tipo === "plt" ? "plt" : "pdf"}`;
      a.click(); setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (err) { toastError(err); }
  };

  // Baixa o corte de UMA folha (quando o corte é diferente por folha).
  const baixarFolha = async (idx) => {
    if (!savedId) return;
    try {
      const { data } = await api.get(`/adesivos/${savedId}/plt?folha=${idx}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `${savedData?.numero || "cartela"}-folha${idx + 1}.plt`;
      a.click(); setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (err) { toastError(err); }
  };

  const baixarTodasFolhas = async () => {
    const n = pltInfo?.numFolhas || 1;
    for (let i = 0; i < n; i++) { await baixarFolha(i); await new Promise((r) => setTimeout(r, 500)); }
  };

  const handleEnviarProva = async () => {
    if (!savedId || !ticketId) return;
    setEnviandoProva(true);
    try {
      await api.post(`/adesivos/${savedId}/send/${ticketId}`);
      setSavedData((d) => ({ ...(d || {}), status: "enviado" }));
      toast.success("Prova enviada ao cliente."); onSaved && onSaved();
    } catch (err) { toastError(err); } finally { setEnviandoProva(false); }
  };

  const handleAprovar = async () => {
    if (!savedId) return;
    setAprovando(true);
    try { const { data } = await api.post(`/adesivos/${savedId}/aprovar`, { aprovada: true }); setSavedData(data); toast.success("Aprovação registrada."); onSaved && onSaved(); }
    catch (err) { toastError(err); } finally { setAprovando(false); }
  };

  const temArquivos = !!savedData?.arquivoPdfFileName;
  const folha = preview.folhas[folhaSel] || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ContentCutIcon color="primary" />
          <span style={{ flex: 1 }}>{savedData?.numero ? `Cartela ${savedData.numero}` : "Nova cartela"} — várias artes na folha</span>
        </div>
      </DialogTitle>
      <DialogContent dividers sx={{ overscrollBehavior: "contain" }}>
        {ticketId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Vinculado ao atendimento{contatoVinculado?.name ? ` de ${contatoVinculado.name}` : ` #${ticketId}`}.
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={5}>
            <TextField label="Descrição (cliente / pedido)" fullWidth size="small"
              value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Ex.: Cliente X — cartela" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Material</InputLabel>
              <Select value={material} label="Material" onChange={(e) => setMaterial(e.target.value)}>
                {opcoes.materiais.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={<Switch checked={mesmaFolha} onChange={(e) => setMesmaFolha(e.target.checked)} />}
              label={mesmaFolha ? "Tudo na mesma folha" : "Folhas separadas por arte"} />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* Coluna esquerda: artes */}
          <Grid item xs={12} md={6}>
            <Divider textAlign="left" sx={{ mb: 1 }}><Typography variant="caption">Artes do pedido</Typography></Divider>
            {itens.map((it, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1, mb: 1 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  {/* Miniatura + upload */}
                  <div style={{ flexShrink: 0, textAlign: "center" }}>
                    <div style={{
                      width: 52, height: 52, border: "1px solid #ddd",
                      borderRadius: it.tipo === "redondo" ? "50%" : 4, overflow: "hidden",
                      background: it.arteUrl ? `center/cover url(${it.arteUrl})` : PALETA[(i + 1) % PALETA.length],
                    }} />
                    <Button component="label" size="small" sx={{ mt: 0.5, minWidth: 0, fontSize: 10 }}
                      startIcon={enviandoArte === i ? <CircularProgress size={12} /> : <UploadFileIcon sx={{ fontSize: 14 }} />}>
                      {it.arteFileName ? "Trocar" : "Arte"}
                      <input type="file" accept="image/png,image/jpeg,application/pdf" hidden onChange={uploadArte(i)} />
                    </Button>
                    {it.arteFileName && (
                      <Button size="small" sx={{ mt: 0.5, minWidth: 0, fontSize: 10, display: "block" }}
                        startIcon={<PaletteIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setVariantesOrigemIdx(i)}>
                        Variantes de cor
                      </Button>
                    )}
                  </div>
                  {/* Campos */}
                  <div style={{ flex: 1 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={5}>
                        <FormControl size="small" fullWidth>
                          <Select value={it.tipo} onChange={(e) => setItem(i, "tipo", e.target.value)}>
                            <MenuItem value="redondo">Redondo</MenuItem>
                            <MenuItem value="quadrado">Quadrado</MenuItem>
                            <MenuItem value="retangular">Retangular</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <TextField label={it.tipo === "redondo" ? "Ø" : "Larg."} type="number" size="small" fullWidth
                          value={it.larguraMm} onChange={setItemNum(i, "larguraMm")}
                          InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField label="Qtd" type="number" size="small" fullWidth
                          value={it.quantidade} onChange={setItemNum(i, "quantidade")} />
                      </Grid>
                      {it.tipo === "retangular" && (
                        <Grid item xs={5}>
                          <TextField label="Altura" type="number" size="small" fullWidth
                            value={it.alturaMm} onChange={setItemNum(i, "alturaMm")}
                            InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
                        </Grid>
                      )}
                      {(it.tipo === "quadrado" || it.tipo === "retangular") && (
                        <Grid item xs={4}>
                          <TextField label="Cantos (raio)" type="number" size="small" fullWidth
                            value={it.raioCantoMm ?? 0} onChange={setItemNum(i, "raioCantoMm")}
                            InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
                        </Grid>
                      )}
                      {/* Ajuste fino da arte na peça (PowerClip) — só quando há arte */}
                      {it.arteFileName && (
                        <>
                          <Grid item xs={6}>
                            <TextField select label="Ajuste da arte" size="small" fullWidth
                              value={it.ajuste || "preencher"} onChange={(e) => setItem(i, "ajuste", e.target.value)}>
                              {AJUSTES.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
                            </TextField>
                          </Grid>
                          <Grid item xs={2}>
                            <TextField label="Escala" type="number" size="small" fullWidth
                              value={it.escalaPct ?? 100} onChange={setItemNum(i, "escalaPct")}
                              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                          </Grid>
                          <Grid item xs={2}>
                            <TextField label="↔ mm" type="number" size="small" fullWidth
                              value={it.offsetXMm ?? 0} onChange={setItemNum(i, "offsetXMm")} />
                          </Grid>
                          <Grid item xs={2}>
                            <TextField label="↕ mm" type="number" size="small" fullWidth
                              value={it.offsetYMm ?? 0} onChange={setItemNum(i, "offsetYMm")} />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </div>
                  <IconButton size="small" onClick={() => rmItem(i)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </div>
              </Paper>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={addItem}>Adicionar arte</Button>

            <Divider textAlign="left" sx={{ my: 2 }}><Typography variant="caption">Produção</Typography></Divider>
            <Grid container spacing={2}>
              <Grid item xs={4}><TextField label="Espaço" type="number" size="small" fullWidth value={espacoMm}
                onChange={(e) => setEspacoMm(Number(e.target.value))} InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} /></Grid>
              <Grid item xs={4}><TextField label="Sangria" type="number" size="small" fullWidth value={sangriaMm}
                onChange={(e) => setSangriaMm(Number(e.target.value))} InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} /></Grid>
              <Grid item xs={4}><TextField label="Preço/folha" type="number" size="small" fullWidth value={precoFolha}
                onChange={(e) => setPrecoFolha(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} /></Grid>
            </Grid>

            {temArquivos && (
              <>
                <Divider sx={{ my: 2 }}><Typography variant="caption">Arquivos gerados</Typography></Divider>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => baixar("pdf")}>Baixar impressão</Button>
                  {pltInfo && pltInfo.cortesIguais === false ? (
                    <Button size="small" variant="outlined" color="warning" startIcon={<ContentCutIcon />} onClick={baixarTodasFolhas}>
                      Baixar {pltInfo.numFolhas} cortes (1/folha)
                    </Button>
                  ) : (
                    <Button size="small" variant="outlined" startIcon={<ContentCutIcon />} onClick={() => baixar("plt")}>Baixar corte (.plt)</Button>
                  )}
                  <Button size="small" variant="text" startIcon={<PictureAsPdfIcon />} onClick={() => baixar("conferencia")}>
                    Conferir corte
                  </Button>
                  {savedData?.arteAprovada
                    ? <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Aprovada" />
                    : <Chip size="small" color="warning" label="Aguardando aprovação" />}
                </div>
                {pltInfo && (
                  pltInfo.cortesIguais
                    ? <Alert severity="success" sx={{ mt: 1, py: 0 }}>
                        ✂️ Corte <b>igual</b> nas {pltInfo.numFolhas} folha(s) — 1 arquivo serve para todas. No pendrive, corte cada folha com ele.
                      </Alert>
                    : <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                        ⚠️ Corte <b>diferente</b> por folha ({pltInfo.numFolhas} folhas). Baixe os {pltInfo.numFolhas} arquivos e corte cada folha impressa com o do <b>mesmo número</b> (folha1 → 1ª folha, folha2 → 2ª...).
                      </Alert>
                )}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                  {ticketId && (
                    <Button size="small" disabled={enviandoProva} onClick={handleEnviarProva}
                      startIcon={enviandoProva ? <CircularProgress size={14} /> : <SendIcon />}>Enviar prova</Button>
                  )}
                  {!savedData?.arteAprovada && (
                    <Button size="small" color="success" disabled={aprovando} onClick={handleAprovar}
                      startIcon={aprovando ? <CircularProgress size={14} /> : <CheckCircleIcon />}>Cliente aprovou</Button>
                  )}
                </div>
              </>
            )}
          </Grid>

          {/* Coluna direita: preview do arranjo (fixo ao rolar, grande) */}
          <Grid item xs={12} md={6} sx={{ position: { md: "sticky" }, top: 8, alignSelf: "flex-start" }}>
            <Typography variant="caption" color="textSecondary">
              Preview — {preview.numFolhas} folha(s) · {preview.totalColocado} adesivos
              {preview.valorTotal > 0 && <> · R$ {preview.valorTotal.toFixed(2).replace(".", ",")}</>}
            </Typography>
            {preview.numFolhas > 1 && (
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", margin: "4px 0" }}>
                {preview.folhas.map((_, i) => (
                  <Chip key={i} size="small" label={`Folha ${i + 1}`} clickable
                    color={folhaSel === i ? "primary" : "default"} onClick={() => setFolhaSel(i)} />
                ))}
              </div>
            )}
            <div style={{
              position: "relative", width: "100%", maxWidth: 460, aspectRatio: `${FOLHA_W} / ${FOLHA_H}`,
              border: "1px solid #ccc", background: "#fff", marginTop: 4,
            }}>
              {/* marcas */}
              {[["2%","2%"],["","2%"],["2%",""],["",""]].map(([l, t], i) => (
                <div key={i} style={{ position: "absolute", left: l || undefined, right: l ? undefined : "2%", top: t || undefined, bottom: t ? undefined : "2%", width: 8, height: 8, border: "1px solid #000" }} />
              ))}
              {folha.map((p, i) => (
                <div key={i} title={`Arte ${p.itemIdx + 1}`} style={{
                  position: "absolute",
                  left: `${p.xPct - p.wPct / 2}%`, top: `${p.yPct - p.hPct / 2}%`,
                  width: `${p.wPct}%`, height: `${p.hPct}%`,
                  background: (itens[p.itemIdx]?.arteUrl) ? `center/cover url(${itens[p.itemIdx].arteUrl})` : PALETA[(p.itemIdx + 1) % PALETA.length],
                  borderRadius: p.tipo === "redondo" ? "50%" : 2, border: "0.5px solid rgba(0,0,0,.3)",
                }} />
              ))}
            </div>
            {preview.faltando > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {preview.faltando} adesivo(s) não couberam nas folhas planejadas — aumente as folhas ou reduza a quantidade.
              </Alert>
            )}
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
              O sistema encaixa as artes automaticamente (grandes primeiro, pequenas ao redor) e pagina em quantas folhas precisar.
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button onClick={handleSalvar} disabled={loading}>Salvar rascunho</Button>
        <Button variant="contained" onClick={handleGerar}
          disabled={gerando || preview.numFolhas === 0}
          startIcon={gerando ? <CircularProgress size={16} /> : <AutorenewIcon />}>
          {temArquivos ? "Regerar" : `Gerar${preview.numFolhas ? ` ${preview.numFolhas} folha(s)` : ""}`}
        </Button>
      </DialogActions>
      <VariantesCorDialog
        open={variantesOrigemIdx !== null}
        onClose={() => setVariantesOrigemIdx(null)}
        arteFileName={variantesOrigemIdx !== null ? itens[variantesOrigemIdx]?.arteFileName : null}
        onGeradas={handleVariantesGeradas}
      />
    </Dialog>
  );
};

export default CartelaDialog;
